import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/** ─── DEMO FLAG: set to false to use live API data ─── */
const USE_MOCK_DATA = true;

/**
 * Mock booking for demo — same patient as ANC schedule (LNMP 2025-09-15, GA ~29+4 wks)
 */
const MOCK_BOOKING_INV = {
    id: 1001,
    patient_number: "DEMO-001",
    lnmp: "2025-09-15",
    edd: "2026-06-22",
    gravida: 2,
    parity: 1,
};

/**
 * Mock investigation results.
 * Covers: first-contact panel (resulted), mid-pregnancy repeats (resulted),
 * 28-wk Hb ordered/pending, urinalysis due/pending at 30 wks.
 */
const MOCK_INVESTIGATIONS = [
    /* ── Serology (first contact, 12 wks) ── */
    {
        id: 301,
        booking_id: 1001,
        investigation_type: "hiv",
        ordered_date: "2025-12-09",
        result_date: "2025-12-10",
        result_value: "Non-reactive",
        result_status: "normal",
        notes: "Counselled; repeat at 32 wks",
        derived_status: "resulted",
        is_abnormal: false,
    },
    {
        id: 302,
        booking_id: 1001,
        investigation_type: "syphilis",
        ordered_date: "2025-12-09",
        result_date: "2025-12-10",
        result_value: "Non-reactive",
        result_status: "normal",
        notes: "",
        derived_status: "resulted",
        is_abnormal: false,
    },
    {
        id: 303,
        booking_id: 1001,
        investigation_type: "hepatitis_b",
        ordered_date: "2025-12-09",
        result_date: "2025-12-10",
        result_value: "Non-reactive",
        result_status: "normal",
        notes: "",
        derived_status: "resulted",
        is_abnormal: false,
    },
    {
        id: 304,
        booking_id: 1001,
        investigation_type: "blood_group",
        ordered_date: "2025-12-09",
        result_date: "2025-12-10",
        result_value: "O+",
        result_status: "normal",
        notes: "",
        derived_status: "resulted",
        is_abnormal: false,
    },
    {
        id: 305,
        booking_id: 1001,
        investigation_type: "sickling",
        ordered_date: "2025-12-09",
        result_date: "2025-12-10",
        result_value: "Negative (AA)",
        result_status: "normal",
        notes: "",
        derived_status: "resulted",
        is_abnormal: false,
    },
    /* ── Haematology — 12-wk Hb ── */
    {
        id: 306,
        booking_id: 1001,
        investigation_type: "haemoglobin",
        ordered_date: "2025-12-09",
        result_date: "2025-12-10",
        result_value: "11.8",
        result_status: "normal",
        notes: "g/dL",
        derived_status: "resulted",
        is_abnormal: false,
    },
    /* ── Haematology — 28-wk Hb (ordered, awaiting result) ── */
    {
        id: 307,
        booking_id: 1001,
        investigation_type: "haemoglobin",
        ordered_date: "2026-04-06",
        result_date: null,
        result_value: null,
        result_status: null,
        notes: "Due at 28 wks",
        derived_status: "pending",
        is_abnormal: false,
    },
    /* ── Biochemistry — blood sugar ── */
    {
        id: 308,
        booking_id: 1001,
        investigation_type: "blood_sugar",
        ordered_date: "2025-12-09",
        result_date: "2025-12-10",
        result_value: "5.2",
        result_status: "normal",
        notes: "RBS mmol/L",
        derived_status: "resulted",
        is_abnormal: false,
    },
    /* ── Urinalysis — 12-wk ── */
    {
        id: 309,
        booking_id: 1001,
        investigation_type: "urinalysis",
        ordered_date: "2025-12-09",
        result_date: "2025-12-09",
        result_value: "Nil abnormality detected",
        result_status: "normal",
        notes: "",
        derived_status: "resulted",
        is_abnormal: false,
    },
    /* ── Urinalysis — 20-wk ── */
    {
        id: 310,
        booking_id: 1001,
        investigation_type: "urinalysis",
        ordered_date: "2026-02-02",
        result_date: "2026-02-02",
        result_value: "Protein +, glucose negative",
        result_status: "abnormal",
        notes: "Mild proteinuria — monitor BP",
        derived_status: "resulted",
        is_abnormal: true,
    },
    /* ── Urinalysis — 26-wk ── */
    {
        id: 311,
        booking_id: 1001,
        investigation_type: "urinalysis",
        ordered_date: "2026-03-17",
        result_date: "2026-03-17",
        result_value: "Nil abnormality detected",
        result_status: "normal",
        notes: "Protein resolved",
        derived_status: "resulted",
        is_abnormal: false,
    },
    /* ── Malaria — 12-wk ── */
    {
        id: 312,
        booking_id: 1001,
        investigation_type: "malaria",
        ordered_date: "2025-12-09",
        result_date: "2025-12-09",
        result_value: "Negative",
        result_status: "normal",
        notes: "",
        derived_status: "resulted",
        is_abnormal: false,
    },
    /* ── Malaria — 20-wk ── */
    {
        id: 313,
        booking_id: 1001,
        investigation_type: "malaria",
        ordered_date: "2026-02-02",
        result_date: "2026-02-02",
        result_value: "Negative",
        result_status: "normal",
        notes: "",
        derived_status: "resulted",
        is_abnormal: false,
    },
    /* ── Radiology — obstetric ultrasound 12wk ── */
    {
        id: 314,
        booking_id: 1001,
        investigation_type: "ultrasound",
        ordered_date: "2025-12-09",
        result_date: "2025-12-16",
        result_value: "Single viable intrauterine pregnancy, GA 12+2 wks, placenta anterior, no anomaly",
        result_status: "normal",
        notes: "",
        derived_status: "resulted",
        is_abnormal: false,
    },
    /* ── Radiology — anomaly scan 20wk ── */
    {
        id: 315,
        booking_id: 1001,
        investigation_type: "ultrasound",
        ordered_date: "2026-02-02",
        result_date: "2026-02-09",
        result_value: "Normal fetal anatomy, EFW 370g, AFI adequate, placenta anterior grade I",
        result_status: "normal",
        notes: "Anomaly scan — no fetal anomaly detected",
        derived_status: "resulted",
        is_abnormal: false,
    },
];

/**
 * Standard ANC Investigation Panel — Uganda MOH recommended
 * Each entry: key, label, category, recommended GA weeks, specimen type
 */
const ANC_PANEL = [
    { key: "hiv", label: "HIV Test", category: "serology", recommendedWeeks: [12, 32], specimen: "Blood" },
    { key: "syphilis", label: "Syphilis (VDRL/RPR)", category: "serology", recommendedWeeks: [12], specimen: "Blood" },
    { key: "hepatitis_b", label: "Hepatitis B (HBsAg)", category: "serology", recommendedWeeks: [12], specimen: "Blood" },
    { key: "blood_group", label: "Blood Group & Rh", category: "haematology", recommendedWeeks: [12], specimen: "Blood" },
    { key: "haemoglobin", label: "Haemoglobin (Hb)", category: "haematology", recommendedWeeks: [12, 28, 36], specimen: "Blood" },
    { key: "blood_sugar", label: "Blood Sugar (RBS/FBS)", category: "biochemistry", recommendedWeeks: [12, 28], specimen: "Blood" },
    { key: "urinalysis", label: "Urinalysis", category: "urinalysis", recommendedWeeks: [12, 20, 26, 30, 34, 36, 38, 40], specimen: "Urine" },
    { key: "malaria", label: "Malaria (mRDT/BS)", category: "parasitology", recommendedWeeks: [12, 20, 26, 30], specimen: "Blood" },
    { key: "sickling", label: "Sickling Test", category: "haematology", recommendedWeeks: [12], specimen: "Blood" },
    { key: "ultrasound", label: "Obstetric Ultrasound", category: "radiology", recommendedWeeks: [12, 20], specimen: "N/A" },
    { key: "viral_load", label: "Viral Load (HIV+)", category: "serology", recommendedWeeks: [], specimen: "Blood", conditional: true },
    { key: "cd4", label: "CD4 Count (HIV+)", category: "serology", recommendedWeeks: [], specimen: "Blood", conditional: true },
];

/**
 * Derive investigation status from data
 */
function deriveStatus(inv) {
    if (inv.result_value && inv.result_date) return "resulted";
    if (inv.ordered_date) return "pending";
    return "not_ordered";
}

/**
 * Check if a result value is abnormal
 */
function checkAbnormal(key, value) {
    if (!value) return false;
    const numVal = parseFloat(value);

    switch (key) {
        case "haemoglobin":
            return !isNaN(numVal) && numVal < 11;
        case "hiv":
            return value.toLowerCase() === "reactive" || value.toLowerCase() === "positive";
        case "syphilis":
            return value.toLowerCase() === "reactive" || value.toLowerCase() === "positive";
        case "hepatitis_b":
            return value.toLowerCase() === "reactive" || value.toLowerCase() === "positive";
        case "malaria":
            return value.toLowerCase() === "positive" || value.toLowerCase().indexOf("positive") >= 0;
        case "blood_sugar":
            return !isNaN(numVal) && (numVal > 11.1 || numVal < 3.5);
        case "urinalysis":
            return value.indexOf("++") >= 0 || value.indexOf("+++") >= 0;
        case "sickling":
            return value.toLowerCase() === "positive" || value.toLowerCase().indexOf("as") >= 0 || value.toLowerCase().indexOf("ss") >= 0;
        default:
            return false;
    }
}

/**
 * useANCInvestigations — Custom hook for ANC Screening & Investigations
 */
export const useANCInvestigations = function(actions, patient_number, visit_id) {

    const _bookingData = useState(null);
    const bookingData = _bookingData[0];
    const setBookingData = _bookingData[1];

    const _loadingBooking = useState(true);
    const loadingBooking = _loadingBooking[0];
    const setLoadingBooking = _loadingBooking[1];

    const _investigations = useState([]);
    const investigations = _investigations[0];
    const setInvestigations = _investigations[1];

    const _loadingInvestigations = useState(false);
    const loadingInvestigations = _loadingInvestigations[0];
    const setLoadingInvestigations = _loadingInvestigations[1];

    const _orderForm = useState({ open: false, investigationType: null });
    const orderForm = _orderForm[0];
    const setOrderForm = _orderForm[1];

    const _resultForm = useState({ open: false, investigation: null });
    const resultForm = _resultForm[0];
    const setResultForm = _resultForm[1];

    const _formData = useState({
        investigation_type: "",
        ordered_date: new Date().toISOString().slice(0, 10),
        result_date: "",
        result_value: "",
        result_status: "",
        notes: "",
    });
    const formData = _formData[0];
    const setFormData = _formData[1];

    const _saving = useState(false);
    const saving = _saving[0];
    const setSaving = _saving[1];

    const _activeView = useState("panel");
    const activeView = _activeView[0];
    const setActiveView = _activeView[1];

    const _filterCategory = useState("all");
    const filterCategory = _filterCategory[0];
    const setFilterCategory = _filterCategory[1];

    /* ─── Fetch booking (or load mock) ─── */
    useEffect(function() {
        if (USE_MOCK_DATA) {
            setBookingData(MOCK_BOOKING_INV);
            setLoadingBooking(false);
            return;
        }
        if (!patient_number) return;
        setLoadingBooking(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/booking/" + patient_number)
            .then(function(res) {
                const data = res.data || {};
                if (data.id) setBookingData(data);
            })
            .catch(function() { setBookingData(null); })
            .finally(function() { setLoadingBooking(false); });
    }, [patient_number]);

    /* ─── Fetch investigations (or load mock) ─── */
    useEffect(function() {
        if (USE_MOCK_DATA) {
            setInvestigations(MOCK_INVESTIGATIONS);
            setLoadingInvestigations(false);
            return;
        }
        if (!bookingData || !bookingData.id) return;
        setLoadingInvestigations(true);
        axios
            .get(config.smsUrl + "/obgyn/anc/investigations/" + bookingData.id)
            .then(function(res) {
                const data = res.data || [];
                if (Array.isArray(data)) {
                    const enriched = data.map(function(inv) {
                        return Object.assign({}, inv, {
                            derived_status: deriveStatus(inv),
                            is_abnormal: checkAbnormal(inv.investigation_type, inv.result_value),
                        });
                    });
                    setInvestigations(enriched);
                }
            })
            .catch(function() { setInvestigations([]); })
            .finally(function() { setLoadingInvestigations(false); });
    }, [bookingData]);

    /* ─── Current gestational age ─── */
    const gestationalAge = useMemo(function() {
        if (!bookingData || !bookingData.lnmp) return null;
        const lnmpDate = new Date(bookingData.lnmp);
        const todayDate = new Date();
        const diffMs = todayDate - lnmpDate;
        if (diffMs < 0) return null;
        const totalDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
        return Math.floor(totalDays / 7);
    }, [bookingData]);

    /* ─── Panel data: merge ANC_PANEL with actual investigations ─── */
    const panelData = useMemo(function() {
        return ANC_PANEL.map(function(panelItem) {
            // Find all investigations of this type
            const matching = investigations.filter(function(inv) {
                return inv.investigation_type === panelItem.key;
            });
            const latest = matching.length > 0 ? matching[matching.length - 1] : null;

            // Determine if due now
            let isDueNow = false;
            if (gestationalAge !== null && panelItem.recommendedWeeks.length > 0) {
                isDueNow = panelItem.recommendedWeeks.some(function(w) {
                    // Due if within 2 weeks of recommended and not yet done at that GA
                    return gestationalAge >= (w - 2) && gestationalAge <= (w + 2);
                });
                // If already resulted recently, not due
                if (latest && latest.derived_status === "resulted") {
                    let resultWeeksAgo = 0;
                    if (latest.result_date) {
                        const rDate = new Date(latest.result_date);
                        const now = new Date();
                        resultWeeksAgo = Math.floor((now - rDate) / (7 * 24 * 60 * 60 * 1000));
                    }
                    if (resultWeeksAgo < 4) isDueNow = false;
                }
            }

            return {
                key: panelItem.key,
                label: panelItem.label,
                category: panelItem.category,
                recommendedWeeks: panelItem.recommendedWeeks,
                specimen: panelItem.specimen,
                conditional: panelItem.conditional || false,
                totalOrdered: matching.length,
                latest: latest,
                status: latest ? latest.derived_status : "not_ordered",
                isAbnormal: latest ? latest.is_abnormal : false,
                resultValue: latest ? latest.result_value : null,
                resultDate: latest ? latest.result_date : null,
                isDueNow: isDueNow,
                allResults: matching,
            };
        });
    }, [investigations, gestationalAge]);

    /* ─── Summary stats ─── */
    const summary = useMemo(function() {
        let resulted = 0;
        let pending = 0;
        let notOrdered = 0;
        let abnormal = 0;
        let dueNow = 0;

        panelData.forEach(function(item) {
            if (item.conditional) return; // Don't count conditional tests in stats
            if (item.status === "resulted") resulted++;
            else if (item.status === "pending") pending++;
            else notOrdered++;
            if (item.isAbnormal) abnormal++;
            if (item.isDueNow && item.status === "not_ordered") dueNow++;
        });

        const total = panelData.filter(function(i) { return !i.conditional; }).length;
        const completionPercent = total > 0 ? Math.round((resulted / total) * 100) : 0;

        return {
            resulted: resulted,
            pending: pending,
            notOrdered: notOrdered,
            abnormal: abnormal,
            dueNow: dueNow,
            total: total,
            completionPercent: completionPercent,
        };
    }, [panelData]);

    /* ─── Filtered panel ─── */
    const filteredPanel = useMemo(function() {
        if (filterCategory === "all") return panelData;
        if (filterCategory === "due") return panelData.filter(function(i) { return i.isDueNow; });
        if (filterCategory === "abnormal") return panelData.filter(function(i) { return i.isAbnormal; });
        if (filterCategory === "pending") return panelData.filter(function(i) { return i.status === "pending"; });
        return panelData.filter(function(i) { return i.category === filterCategory; });
    }, [panelData, filterCategory]);

    /* ─── Handle form change ─── */
    const handleFormChange = useCallback(function(field, value) {
        setFormData(function(prev) {
            return Object.assign({}, prev, (function() { const o = {}; o[field] = value; return o; })());
        });
    }, []);

    /* ─── Open order form ─── */
    const openOrderForm = useCallback(function(investigationType) {
        setFormData({
            investigation_type: investigationType || "",
            ordered_date: new Date().toISOString().slice(0, 10),
            result_date: "",
            result_value: "",
            result_status: "",
            notes: "",
        });
        setOrderForm({ open: true, investigationType: investigationType });
    }, []);

    const closeOrderForm = useCallback(function() {
        setOrderForm({ open: false, investigationType: null });
    }, []);

    /* ─── Open result form ─── */
    const openResultForm = useCallback(function(investigation) {
        setFormData({
            investigation_type: investigation.investigation_type || investigation.key || "",
            ordered_date: investigation.ordered_date || "",
            result_date: new Date().toISOString().slice(0, 10),
            result_value: investigation.result_value || "",
            result_status: investigation.result_status || "",
            notes: investigation.notes || "",
        });
        setResultForm({ open: true, investigation: investigation });
    }, []);

    const closeResultForm = useCallback(function() {
        setResultForm({ open: false, investigation: null });
    }, []);

    /* ─── Submit order ─── */
    const submitOrder = useCallback(function() {
        if (!bookingData || !formData.investigation_type) return;
        setSaving(true);

        const payload = {
            booking_id: bookingData.id,
            visit_id: visit_id,
            investigation_type: formData.investigation_type,
            ordered_date: formData.ordered_date,
            notes: formData.notes,
        };

        axios
            .post(config.smsUrl + "/obgyn/anc/investigation", payload)
            .then(function(res) {
                if (actions && actions.snackbarActions) {
                    const panelItem = ANC_PANEL.find(function(p) { return p.key === formData.investigation_type; });
                    actions.snackbarActions.snackbarOpen("success", (panelItem ? panelItem.label : "Investigation") + " ordered successfully");
                }
                // Add to local state
                const newInv = Object.assign({}, res.data || payload, {
                    derived_status: "pending",
                    is_abnormal: false,
                });
                setInvestigations(function(prev) { return prev.concat([newInv]); });
                closeOrderForm();
            })
            .catch(function() {
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackbarOpen("error", "Failed to order investigation");
                }
            })
            .finally(function() { setSaving(false); });
    }, [bookingData, visit_id, formData, actions]);

    /* ─── Submit result ─── */
    const submitResult = useCallback(function() {
        if (!resultForm.investigation) return;
        setSaving(true);

        const inv = resultForm.investigation;
        const invId = inv.id || (inv.latest && inv.latest.id);

        if (!invId) {
            // Create new with result included
            const payload = {
                booking_id: bookingData ? bookingData.id : null,
                visit_id: visit_id,
                investigation_type: formData.investigation_type,
                ordered_date: formData.ordered_date || new Date().toISOString().slice(0, 10),
                result_date: formData.result_date,
                result_value: formData.result_value,
                result_status: formData.result_status || (checkAbnormal(formData.investigation_type, formData.result_value) ? "abnormal" : "normal"),
                notes: formData.notes,
            };

            axios
                .post(config.smsUrl + "/obgyn/anc/investigation", payload)
                .then(function(res) {
                    if (actions && actions.snackbarActions) {
                        actions.snackbarActions.snackbarOpen("success", "Result recorded successfully");
                    }
                    const newInv = Object.assign({}, res.data || payload, {
                        derived_status: "resulted",
                        is_abnormal: checkAbnormal(payload.investigation_type, payload.result_value),
                    });
                    setInvestigations(function(prev) { return prev.concat([newInv]); });
                    closeResultForm();
                })
                .catch(function() {
                    if (actions && actions.snackbarActions) {
                        actions.snackbarActions.snackbarOpen("error", "Failed to save result");
                    }
                })
                .finally(function() { setSaving(false); });
        } else {
            // Update existing
            const updatePayload = {
                result_date: formData.result_date,
                result_value: formData.result_value,
                result_status: formData.result_status || (checkAbnormal(formData.investigation_type, formData.result_value) ? "abnormal" : "normal"),
                notes: formData.notes,
            };

            axios
                .put(config.smsUrl + "/obgyn/anc/investigation/" + invId, updatePayload)
                .then(function() {
                    if (actions && actions.snackbarActions) {
                        actions.snackbarActions.snackbarOpen("success", "Result updated successfully");
                    }
                    setInvestigations(function(prev) {
                        return prev.map(function(item) {
                            if (item.id === invId) {
                                return Object.assign({}, item, updatePayload, {
                                    derived_status: "resulted",
                                    is_abnormal: checkAbnormal(item.investigation_type, updatePayload.result_value),
                                });
                            }
                            return item;
                        });
                    });
                    closeResultForm();
                })
                .catch(function() {
                    if (actions && actions.snackbarActions) {
                        actions.snackbarActions.snackbarOpen("error", "Failed to update result");
                    }
                })
                .finally(function() { setSaving(false); });
        }
    }, [resultForm, bookingData, visit_id, formData, actions]);

    /* ─── Quick-order standard panel ─── */
    const quickOrderPanel = useCallback(function() {
        if (!bookingData) return;
        setSaving(true);

        const standardTests = ANC_PANEL.filter(function(t) {
            return !t.conditional && t.recommendedWeeks.indexOf(12) >= 0;
        });
        const notYetOrdered = standardTests.filter(function(t) {
            const existing = investigations.find(function(inv) { return inv.investigation_type === t.key; });
            return !existing;
        });

        if (notYetOrdered.length === 0) {
            if (actions && actions.snackbarActions) {
                actions.snackbarActions.snackbarOpen("info", "All standard first-visit tests have already been ordered");
            }
            setSaving(false);
            return;
        }

        const promises = notYetOrdered.map(function(t) {
            return axios.post(config.smsUrl + "/obgyn/anc/investigation", {
                booking_id: bookingData.id,
                visit_id: visit_id,
                investigation_type: t.key,
                ordered_date: new Date().toISOString().slice(0, 10),
            });
        });

        Promise.all(promises)
            .then(function(results) {
                const newInvs = results.map(function(res, idx) {
                    return Object.assign({}, res.data || {
                        investigation_type: notYetOrdered[idx].key,
                        ordered_date: new Date().toISOString().slice(0, 10),
                    }, { derived_status: "pending", is_abnormal: false });
                });
                setInvestigations(function(prev) { return prev.concat(newInvs); });
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackbarOpen("success", notYetOrdered.length + " investigations ordered successfully");
                }
            })
            .catch(function() {
                if (actions && actions.snackbarActions) {
                    actions.snackbarActions.snackbarOpen("error", "Failed to order some investigations");
                }
            })
            .finally(function() { setSaving(false); });
    }, [bookingData, visit_id, investigations, actions]);

    return {
        bookingData: bookingData,
        loadingBooking: loadingBooking,
        investigations: investigations,
        loadingInvestigations: loadingInvestigations,
        panelData: panelData,
        filteredPanel: filteredPanel,
        summary: summary,
        gestationalAge: gestationalAge,
        activeView: activeView,
        setActiveView: setActiveView,
        filterCategory: filterCategory,
        setFilterCategory: setFilterCategory,
        orderForm: orderForm,
        openOrderForm: openOrderForm,
        closeOrderForm: closeOrderForm,
        resultForm: resultForm,
        openResultForm: openResultForm,
        closeResultForm: closeResultForm,
        formData: formData,
        handleFormChange: handleFormChange,
        submitOrder: submitOrder,
        submitResult: submitResult,
        quickOrderPanel: quickOrderPanel,
        saving: saving,
        ANC_PANEL: ANC_PANEL,
        checkAbnormal: checkAbnormal,
    };
};
